// Simmons University: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Simmons posts no current Common Data Set: its Common Data Set page
//    shows the 2020-2021 edition only. The first-year counts come from the
//    U.S. Department of Education's IPEDS Admissions survey for Fall 2024,
//    first-time degree-seeking students (College Navigator and the ADM2024
//    complete data file, unit 167783: 4,773 applied, 3,340 admitted, 361
//    enrolled).
//  - Simmons Undergraduate Admission: First-Year Applicants (requirements,
//    "Applying is free", SAT or ACT optional, recommended preparation),
//    Application Deadlines for Undergraduate Students (first-year plans and
//    notification dates; the table carries no year, so it is read as the
//    deadlines for the application now open, fall 2027 entry), and
//    Admission Policies (who may apply to the women-centered undergraduate
//    program).
//  - Simmons, Cost of Attendance for Undergraduate Students, 2026-2027,
//    Traditional Undergraduate, on campus. The rows are the non-nursing
//    figures; nursing tuition is higher and is given in the note.
//  - College Scorecard, school id 167783 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// No announced change for fall 2028 entry was found on the admissions pages.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=167783#admsns',
        read: '2026-10-01',
    },
    cdsPage: {
        label: 'Simmons University, Common Data Set',
        url: 'https://www.simmons.edu/why-simmons/common-data-set',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Simmons Admission, First-Year Applicants',
        url: 'https://www.simmons.edu/undergraduate/admission-and-financial-aid/how-apply/first-year-applicants/admission-requirements',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Simmons Admission, Application Deadlines for Undergraduate Students',
        url: 'https://www.simmons.edu/undergraduate/admission-and-financial-aid/how-apply/application-deadlines-undergraduate-students',
        read: '2026-10-01',
    },
    policies: {
        label: 'Simmons Admission, Admission Policies',
        url: 'https://www.simmons.edu/admission-aid/application-process/admission-policies',
        read: '2026-10-01',
    },
    costs: {
        label: 'Simmons, Cost of Attendance for Undergraduate Students',
        url: 'https://www.simmons.edu/admission-aid/tuition-costs/tuition-fees-undergraduate-students/cost-attendance-undergraduate',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?167783-Simmons-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'simmons-university',
    kbId: 'simmons_university',
    name: 'Simmons University',
    shortName: 'Simmons',
    location: 'Boston, MA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Simmons brand color palette); decoration only.
    branding: {
        primary: '#003A70',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/simmons_university_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 4773,
        admitted: 3340,
        enrolled: 361,
        source: 'ipeds',
        where: 'first-time, degree-seeking undergraduates',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Simmons publishes no first-year acceptance rate by school or major, and no current Common Data Set: its Common Data Set page shows the 2020-2021 edition only, so the figures above come from the federal IPEDS survey.',
        source: 'cdsPage',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Simmons through the Common App, and applying is free. A few things differ:',
        rows: [
            { school: 'Who may apply', requirement: 'The undergraduate program is a women\'s college. Simmons considers eligible individuals who were assigned female at birth, applicants who consistently live and identify as women, and applicants who do not fit into the gender binary but who feel they belong in its undergraduate community of women.', source: 'policies' },
            { school: 'Nursing', requirement: 'A Priority Nursing deadline of November 1, with notification in mid to late December. On the Common App, choose "Early Action 1/Priority Nursing" as the preferred admission plan.', source: 'deadlines' },
            { school: 'Nursing, nutrition and physical therapy', requirement: 'Simmons recommends four years of high school science, one more than for other applicants.', source: 'firstYear' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Priority Nursing', deadline: 'November 1', decision: 'Mid to late December', binding: false },
            { plan: 'Fall Early Action 1', deadline: 'November 1', decision: 'Mid to late December', binding: false },
            { plan: 'Fall Early Action 2', deadline: 'December 1', decision: 'Mid to late January', binding: false },
            { plan: 'Fall Regular Decision', deadline: 'February 1', decision: 'Rolling through February, March and April', binding: false },
        ],
        note: 'The Simmons Distinguished Scholar Award has its own December 1 deadline, with notification in mid to late January.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Simmons does not require the SAT or ACT. The first-year application asks for a high school transcript, a teacher recommendation, a counselor recommendation and a college essay, and you can also choose to submit SAT or ACT scores.',
        source: 'firstYear',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 48308 },
            { item: 'Fees', amount: 1340 },
            { item: 'Housing and food (on campus)', amount: 18960 },
            { item: 'Books and supplies', amount: 1280 },
            { item: 'Personal expenses', amount: 1700 },
            { item: 'Transportation', amount: 850 },
        ],
        total: 72438,
        note: 'For a full-time traditional undergraduate in a non-nursing program living on campus. Nursing tuition is $52,930, for a total of $77,060. Books, personal and transportation figures are Simmons\'s estimates.',
        source: 'costs',
        netPrice: {
            average: 23444,
            byIncome: [
                { income: '$0 to $30,000', amount: 19035 },
                { income: '$30,001 to $48,000', amount: 18732 },
                { income: '$48,001 to $75,000', amount: 17108 },
                { income: '$75,001 to $110,000', amount: 22495 },
                { income: 'Over $110,000', amount: 34113 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Simmons\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
